/**
 * HighlightText Component
 * 
 * Highlights matching text within a string.
 * Used in search results to show where matches occur.
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';

// ========================================
// Styled Components
// ========================================

const HighlightMark = styled.mark`
  background-color: #FEF08A;
  color: inherit;
  padding: 0 2px;
  margin: 0 -2px;
  border-radius: 2px;
`;

const TextWrapper = styled.span`
  word-break: break-word;
`;

// ========================================
// Types
// ========================================

export interface HighlightTextProps {
  text: string;
  searchText: string;
  caseSensitive?: boolean;
}

// ========================================
// Component
// ========================================

const HighlightText: React.FC<HighlightTextProps> = ({
  text,
  searchText,
  caseSensitive = false,
}) => {
  const highlightedContent = useMemo(() => {
    if (!searchText || !text) {
      return text;
    }

    const searchTerm = searchText.trim();
    if (!searchTerm) {
      return text;
    }

    // Escape special regex characters
    const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Create regex with case sensitivity option
    const regex = new RegExp(`(${escapedSearch})`, caseSensitive ? 'g' : 'gi');
    
    // Split text by matches
    const parts = text.split(regex);
    
    if (parts.length === 1) {
      // No matches found
      return text;
    }

    return parts.map((part, index) => {
      // Check if this part matches the search text
      const isMatch = caseSensitive 
        ? part === searchTerm 
        : part.toLowerCase() === searchTerm.toLowerCase();
      
      if (isMatch) {
        return <HighlightMark key={index}>{part}</HighlightMark>;
      }
      return part;
    });
  }, [text, searchText, caseSensitive]);

  return <TextWrapper>{highlightedContent}</TextWrapper>;
};

export default HighlightText;
